import { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";

const CATEGORIES = [
  "U7", "U9", "U11", "U13", "U15", "U17", "U19", "Seniors", "Loisirs",
];

const EMPTY = {
  child_first_name: "",
  child_last_name: "",
  child_birthdate: "",
  child_gender: "",
  category: "",
  position: "",
  registration_type: "new",
  previous_club: "",
  mutation_needed: false,
  parent_first_name: "",
  parent_last_name: "",
  parent_email: "",
  parent_phone: "",
  address: "",
  postal_code: "",
  city: "Villeurbanne",
  notes: "",
  rgpd_consent: false,
};

export default function PreInscriptionForm() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      // For Seniors (adults), auto-copy the player's name into parent_* so the
      // notification email and admin UI still display a sender name coherently.
      const payload = form.category === "Seniors"
        ? { ...form, parent_first_name: form.child_first_name, parent_last_name: form.child_last_name }
        : form;
      await api.post("/registrations", payload);
      setStatus("success");
      setForm(EMPTY);
      window.scrollTo({ top: document.getElementById("preinscription-form").offsetTop - 60, behavior: "smooth" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez ou contactez le président.");
    }
  };

  if (status === "success") {
    return (
      <div id="preinscription-form" className="rounded-lg border-2 border-emerald-500 bg-emerald-50 p-8 lg:p-10 text-center" data-testid="preinscription-success">
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
        <h3 className="mt-4 text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>Pré-inscription envoyée !</h3>
        <p className="mt-3 text-sm text-emerald-800 max-w-xl mx-auto leading-relaxed">
          Merci, votre demande a bien été transmise au président du club. Vous serez recontacté très prochainement par email ou téléphone
          pour finaliser le dossier (pièces à fournir, dépôt, règlement). À bientôt sur les terrains du Villeurbanne United FC !
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          data-testid="preinscription-new-btn"
          className="mt-6 px-5 py-2.5 rounded-md bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-colors text-sm"
        >
          Soumettre une autre pré-inscription
        </button>
      </div>
    );
  }

  const input = "w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40 focus:border-[#1E3A8A] transition-colors";
  const label = "block text-xs font-semibold text-[#0F172A] uppercase tracking-wider mb-1.5";
  const isSenior = form.category === "Seniors";

  return (
    <form onSubmit={submit} id="preinscription-form" className="rounded-lg border border-[#E2E8F0] bg-white p-6 lg:p-10 space-y-8" data-testid="preinscription-form">
      {/* Type d'inscription */}
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold mb-3">Type de demande</div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { v: "new", label: "Nouvelle inscription", desc: "Jamais licencié au VUFC" },
            { v: "renewal", label: "Renouvellement", desc: "Déjà licencié la saison passée" },
          ].map((o) => (
            <label
              key={o.v}
              className={`cursor-pointer rounded-md border-2 p-4 transition-all ${form.registration_type === o.v ? "border-[#1E3A8A] bg-blue-50" : "border-[#E2E8F0] hover:border-[#CBD5E1]"}`}
              data-testid={`type-${o.v}`}
            >
              <input type="radio" name="registration_type" value={o.v} checked={form.registration_type === o.v} onChange={(e) => update("registration_type", e.target.value)} className="sr-only" />
              <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>{o.label}</div>
              <div className="text-xs text-[#475569] mt-1">{o.desc}</div>
            </label>
          ))}
        </div>
      </div>

      {/* Enfant / Joueur */}
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold mb-4">
          {isSenior ? "Vos informations" : "Informations du joueur"}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={label} htmlFor="child_first_name">Prénom <span className="text-[#DC2626]">*</span></label>
            <input id="child_first_name" required value={form.child_first_name} onChange={(e) => update("child_first_name", e.target.value)} className={input} data-testid="child-first-name" />
          </div>
          <div>
            <label className={label} htmlFor="child_last_name">Nom <span className="text-[#DC2626]">*</span></label>
            <input id="child_last_name" required value={form.child_last_name} onChange={(e) => update("child_last_name", e.target.value)} className={input} data-testid="child-last-name" />
          </div>
          <div>
            <label className={label} htmlFor="child_birthdate">Date de naissance <span className="text-[#DC2626]">*</span></label>
            <input id="child_birthdate" type="date" required value={form.child_birthdate} onChange={(e) => update("child_birthdate", e.target.value)} className={input} data-testid="child-birthdate" />
          </div>
          <div>
            <label className={label} htmlFor="child_gender">Sexe</label>
            <select id="child_gender" value={form.child_gender} onChange={(e) => update("child_gender", e.target.value)} className={input} data-testid="child-gender">
              <option value="">—</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="category">Catégorie souhaitée <span className="text-[#DC2626]">*</span></label>
            <select id="category" required value={form.category} onChange={(e) => update("category", e.target.value)} className={input} data-testid="category">
              <option value="">Sélectionnez une catégorie</option>
              {CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <p className="mt-1.5 text-xs text-[#475569]">Si vous hésitez, le staff vous confirmera selon l'âge de l'enfant.</p>
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="position">Poste préféré</label>
            <select id="position" value={form.position} onChange={(e) => update("position", e.target.value)} className={input} data-testid="position">
              <option value="">Indifférent / À définir avec le coach</option>
              <option value="Gardien de but">Gardien de but</option>
              <option value="Défenseur">Défenseur</option>
              <option value="Milieu de terrain">Milieu de terrain</option>
              <option value="Attaquant">Attaquant</option>
              <option value="Polyvalent">Polyvalent</option>
            </select>
            <p className="mt-1.5 text-xs text-[#475569]">Indication seulement — le coach confirmera après la séance d'essai.</p>
          </div>
        </div>
      </div>

      {/* Historique sportif */}
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold mb-4">Historique sportif</div>
        <div className="space-y-4">
          <div>
            <label className={label} htmlFor="previous_club">Ancien club (si existant)</label>
            <input id="previous_club" value={form.previous_club} onChange={(e) => update("previous_club", e.target.value)} placeholder="Nom du club précédent — laissez vide si aucun" className={input} data-testid="previous-club" />
          </div>
          {form.previous_club && (
            <label className="flex items-start gap-3 p-4 bg-[#FEF3C7] border border-[#D97706]/30 rounded-md cursor-pointer" data-testid="mutation-wrapper">
              <input type="checkbox" checked={form.mutation_needed} onChange={(e) => update("mutation_needed", e.target.checked)} className="mt-1 w-4 h-4 accent-[#D97706]" data-testid="mutation-checkbox" />
              <div>
                <div className="font-bold text-[#92400E]" style={{ fontFamily: "Outfit" }}>Mutation nécessaire</div>
                <div className="text-xs text-[#92400E]/90 mt-1">Cochez si une procédure de mutation FFF est à prévoir depuis l'ancien club (à partir des U10).</div>
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Parent / Tuteur légal — ou coordonnées personnelles si Senior */}
      <div>
        <div className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold mb-1">
          {isSenior ? "Informations personnelles" : "Parent / Tuteur légal"}
        </div>
        <div className="text-xs text-[#475569] mb-4">
          {isSenior
            ? "Vous êtes majeur : indiquez vos coordonnées personnelles — pas besoin d'informations parentales."
            : "Coordonnées du parent ou tuteur légal qui s'occupe du dossier."}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {!isSenior && (
            <>
              <div>
                <label className={label} htmlFor="parent_first_name">Prénom <span className="text-[#DC2626]">*</span></label>
                <input id="parent_first_name" required value={form.parent_first_name} onChange={(e) => update("parent_first_name", e.target.value)} className={input} data-testid="parent-first-name" />
              </div>
              <div>
                <label className={label} htmlFor="parent_last_name">Nom <span className="text-[#DC2626]">*</span></label>
                <input id="parent_last_name" required value={form.parent_last_name} onChange={(e) => update("parent_last_name", e.target.value)} className={input} data-testid="parent-last-name" />
              </div>
            </>
          )}
          <div>
            <label className={label} htmlFor="parent_email">Email <span className="text-[#DC2626]">*</span></label>
            <input id="parent_email" type="email" required value={form.parent_email} onChange={(e) => update("parent_email", e.target.value)} className={input} data-testid="parent-email" />
          </div>
          <div>
            <label className={label} htmlFor="parent_phone">Téléphone <span className="text-[#DC2626]">*</span></label>
            <input id="parent_phone" type="tel" required value={form.parent_phone} onChange={(e) => update("parent_phone", e.target.value)} placeholder="06 __ __ __ __" className={input} data-testid="parent-phone" />
          </div>
          <div className="sm:col-span-2">
            <label className={label} htmlFor="address">Adresse</label>
            <input id="address" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="N° et rue" className={input} data-testid="address" />
          </div>
          <div>
            <label className={label} htmlFor="postal_code">Code postal</label>
            <input id="postal_code" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} placeholder="69100" className={input} data-testid="postal-code" />
          </div>
          <div>
            <label className={label} htmlFor="city">Ville</label>
            <input id="city" value={form.city} onChange={(e) => update("city", e.target.value)} className={input} data-testid="city" />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className={label} htmlFor="notes">Message complémentaire (facultatif)</label>
        <textarea id="notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Allergies, besoins particuliers, question à poser au président…" className={input + " resize-y"} data-testid="notes" />
      </div>

      {/* RGPD */}
      <label className="flex items-start gap-3 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md cursor-pointer" data-testid="rgpd-wrapper">
        <input type="checkbox" required checked={form.rgpd_consent} onChange={(e) => update("rgpd_consent", e.target.checked)} className="mt-1 w-4 h-4 accent-[#1E3A8A]" data-testid="rgpd-checkbox" />
        <div className="text-xs text-[#475569] leading-relaxed">
          J'autorise le <strong>Villeurbanne United FC</strong> à conserver et traiter {isSenior ? "mes informations" : "les informations ci-dessus"} dans le cadre de {isSenior ? "ma" : "la"} pré-inscription {isSenior ? "" : "de mon enfant "}au club.
          Ces données ne seront pas partagées à des tiers. {isSenior ? "Je" : "Je"} pourrai demander leur suppression à tout moment en écrivant à <a href="mailto:presidencevufc@yahoo.com" className="text-[#1E3A8A] font-semibold">presidencevufc@yahoo.com</a>.
        </div>
      </label>

      {status === "error" && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-800" data-testid="preinscription-error">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>{errorMsg}</div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
        <p className="text-xs text-[#475569]"><span className="text-[#DC2626]">*</span> Champs obligatoires</p>
        <button
          type="submit"
          disabled={status === "sending"}
          data-testid="preinscription-submit"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#D97706] hover:bg-[#B45309] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition-colors"
        >
          {status === "sending" ? (<><Loader2 size={16} className="animate-spin" /> Envoi en cours…</>) : (<><Send size={16} /> Envoyer la pré-inscription</>)}
        </button>
      </div>
    </form>
  );
}
